import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import JSZip from "jszip";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, Download, Archive, Copy, Loader2 } from "lucide-react";

/**
 * WhatsAppShareDialog — envoie un véhicule sur WhatsApp en 3 formats :
 *   1. Une carte composite JPG (2x2 photos + prix + QR + logo)
 *   2. Un ZIP de toutes les photos (à glisser dans WhatsApp Web)
 *   3. Un message texte pré-rédigé (ouvre WhatsApp avec lien)
 */
export default function WhatsAppShareDialog({ open, onClose, vehicle }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [zipping, setZipping] = useState(false);
  const canvasRef = useRef(null);

  const priceLabel = vehicle?.price && vehicle.price > 0
    ? new Intl.NumberFormat("fr-BE").format(vehicle.price) + " €"
    : "Prix sur demande";
  const kmLabel = new Intl.NumberFormat("fr-BE").format(vehicle?.mileage || 0) + " km";
  const shareUrl = typeof window !== "undefined" && vehicle
    ? `${window.location.origin}/stock/${vehicle.id}`
    : "";
  const message = vehicle
    ? `🏁 *${vehicle.brand} ${vehicle.model}* — ${vehicle.year}\n\n· ${kmLabel}\n· ${vehicle.power_hp || "-"} ch · ${vehicle.transmission || ""}\n· ${vehicle.fuel_type || ""}\n\n💰 ${priceLabel}\n\n📸 Photos & fiche complète :\n${shareUrl}\n\nKV International — Rhode-Saint-Genèse`
    : "";

  /* Build composite JPG once dialog opens */
  useEffect(() => {
    if (!open || !vehicle) return;
    setPreviewUrl("");
    buildComposite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vehicle?.id]);

  const loadImg = (src) => new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error("img fail: " + src));
    im.src = src;
  });

  const drawCover = (ctx, im, x, y, w, h) => {
    const ir = im.width / im.height;
    const dr = w / h;
    let sw, sh, sx, sy;
    if (ir > dr) { sh = im.height; sw = sh * dr; sx = (im.width - sw) / 2; sy = 0; }
    else        { sw = im.width;  sh = sw / dr; sx = 0; sy = (im.height - sh) / 2; }
    ctx.drawImage(im, sx, sy, sw, sh, x, y, w, h);
  };

  const buildComposite = async () => {
    const imgs = (vehicle.images || []).slice(0, 4);
    if (imgs.length === 0) return;
    const W = 1200, H = 1500;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    // background
    ctx.fillStyle = "#0D0D0D"; ctx.fillRect(0, 0, W, H);

    // photo mosaic 2x2 in top 900px
    const pad = 16, gap = 12;
    const gridW = W - pad * 2, gridH = 900 - pad * 2;
    const cellW = (gridW - gap) / 2, cellH = (gridH - gap) / 2;
    const positions = [
      [pad, pad], [pad + cellW + gap, pad],
      [pad, pad + cellH + gap], [pad + cellW + gap, pad + cellH + gap],
    ];
    try {
      const loaded = await Promise.all(imgs.map((u) => loadImg(u).catch(() => null)));
      loaded.forEach((im, i) => {
        if (!im) return;
        drawCover(ctx, im, positions[i][0], positions[i][1], cellW, cellH);
      });
      // if less than 4 photos, fill blanks with dark gold gradient
      for (let i = loaded.length; i < 4; i++) {
        ctx.fillStyle = "#1E1E1E";
        ctx.fillRect(positions[i][0], positions[i][1], cellW, cellH);
      }
    } catch (e) {
      // ignore - keep dark bg
    }

    // info panel bottom 600px
    ctx.fillStyle = "#0D0D0D"; ctx.fillRect(0, 900, W, 600);
    ctx.fillStyle = "#C8A75A"; ctx.fillRect(0, 900, W, 3);

    // brand/kicker
    ctx.fillStyle = "#C8A75A";
    ctx.font = "600 22px 'Helvetica Neue', Arial, sans-serif";
    ctx.textBaseline = "top";
    ctx.fillText(`${(vehicle.brand || "").toUpperCase()} · ${vehicle.year || ""}`, 40, 940);

    // model title
    ctx.fillStyle = "#ffffff";
    ctx.font = "300 64px 'Times New Roman', Georgia, serif";
    const modelText = vehicle.model || "";
    ctx.fillText(modelText.length > 32 ? modelText.slice(0, 32) + "…" : modelText, 40, 980);

    // specs strip
    ctx.font = "400 22px 'Helvetica Neue', Arial, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    const specs = [
      kmLabel,
      (vehicle.power_hp ? `${vehicle.power_hp} ch` : null),
      vehicle.transmission,
      vehicle.fuel_type,
    ].filter(Boolean).join("   ·   ");
    ctx.fillText(specs, 40, 1080);

    // separator
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(40, 1140); ctx.lineTo(W - 40, 1140); ctx.stroke();

    // price
    ctx.font = "300 78px 'Times New Roman', Georgia, serif";
    ctx.fillStyle = "#C8A75A";
    ctx.fillText(priceLabel, 40, 1180);

    // right column: QR + brand
    const qrSize = 260;
    const qrX = W - qrSize - 40;
    const qrY = 1170;
    // draw white background for QR
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(qrX - 12, qrY - 12, qrSize + 24, qrSize + 24);

    // Render offscreen SVG QR and draw
    try {
      const svgHost = document.getElementById("wa-hidden-qr");
      if (svgHost) {
        const svgEl = svgHost.querySelector("svg");
        if (svgEl) {
          const xml = new XMLSerializer().serializeToString(svgEl);
          const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
          const url = URL.createObjectURL(svgBlob);
          const qim = await loadImg(url);
          ctx.drawImage(qim, qrX, qrY, qrSize, qrSize);
          URL.revokeObjectURL(url);
        }
      }
    } catch (e) { /* ignore */ }

    // "Scannez" hint below QR
    ctx.font = "500 14px 'Helvetica Neue', Arial, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.textAlign = "center";
    ctx.fillText("Scannez pour la fiche complète", qrX + qrSize / 2, qrY + qrSize + 12);
    ctx.textAlign = "start";

    // footer "KV International"
    ctx.font = "700 18px 'Helvetica Neue', Arial, sans-serif";
    ctx.fillStyle = "#C8A75A";
    ctx.textAlign = "center";
    ctx.fillText("KV INTERNATIONAL · Rhode-Saint-Genèse", W / 2, H - 40);
    ctx.textAlign = "start";

    setPreviewUrl(canvas.toDataURL("image/jpeg", 0.92));
  };

  const downloadComposite = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `kv-${vehicle.brand}-${vehicle.model}-${vehicle.id.slice(0, 6)}.jpg`.replace(/\s+/g, "-").toLowerCase();
    a.click();
    toast.success("Carte JPG téléchargée");
  };

  const downloadZip = async () => {
    if (!vehicle?.images?.length) { toast.error("Aucune photo à télécharger"); return; }
    setZipping(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder(`kv-${vehicle.brand}-${vehicle.model}`.replace(/\s+/g, "-").toLowerCase());
      let idx = 1;
      for (const url of vehicle.images) {
        try {
          const resp = await fetch(url, { mode: "cors" });
          if (!resp.ok) continue;
          const blob = await resp.blob();
          const extMatch = url.split("?")[0].match(/\.(jpg|jpeg|png|webp)$/i);
          const ext = extMatch ? extMatch[1].toLowerCase() : "jpg";
          folder.file(`photo-${String(idx).padStart(2, "0")}.${ext}`, blob);
          idx += 1;
        } catch { /* skip failed */ }
      }
      if (previewUrl) {
        const base64 = previewUrl.split(",")[1];
        folder.file("00-carte-partage.jpg", base64, { base64: true });
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kv-${vehicle.brand}-${vehicle.model}-photos.zip`.replace(/\s+/g, "-").toLowerCase();
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${idx - 1} photos téléchargées`);
    } catch (e) {
      toast.error("Erreur téléchargement ZIP");
    } finally {
      setZipping(false);
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener");
  };

  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(message); toast.success("Message copié"); }
    catch { toast.error("Copie impossible"); }
  };

  if (!vehicle) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="wa-share-dialog" className="bg-[#1E1E1E] border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">Envoyer sur WhatsApp</DialogTitle>
          <DialogDescription className="text-white/50 text-xs">Carte JPG composite, ZIP photos et message pré-rédigé pour ce véhicule.</DialogDescription>
        </DialogHeader>

        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75A]">{vehicle.brand} · {vehicle.year}</p>
        <p className="text-white/70 text-sm mt-1">{vehicle.model} · {priceLabel}</p>

        {/* Hidden QR for composite rendering */}
        <div id="wa-hidden-qr" style={{ position: "fixed", left: -9999, top: -9999 }}>
          <QRCodeSVG value={shareUrl} size={260} level="M" bgColor="#ffffff" fgColor="#0D0D0D" />
        </div>

        {/* preview */}
        <div className="mt-6 bg-[#0D0D0D] border border-white/10 rounded-md p-3 flex items-center justify-center min-h-[260px]">
          {previewUrl ? (
            <img data-testid="wa-preview" src={previewUrl} alt="Aperçu carte partage" className="max-h-[320px] rounded shadow-lg" />
          ) : (
            <div className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Génération de la carte…</div>
          )}
        </div>
        <canvas ref={canvasRef} style={{ display: "none" }} />

        {/* Message preview */}
        <div className="mt-6">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">Message WhatsApp pré-rédigé</p>
          <div className="bg-[#0D0D0D] border border-white/10 rounded-md p-4 text-white/80 text-sm whitespace-pre-wrap font-mono">{message}</div>
        </div>

        {/* Actions */}
        <div className="grid md:grid-cols-4 gap-2 mt-6">
          <button data-testid="wa-open" onClick={openWhatsApp} className="flex flex-col items-center justify-center gap-1 bg-[#25D366] text-black hover:bg-white px-3 py-4 rounded-md text-xs uppercase tracking-widest font-semibold">
            <MessageCircle className="w-5 h-5" />
            Ouvrir WhatsApp
          </button>
          <button data-testid="wa-download-jpg" onClick={downloadComposite} disabled={!previewUrl} className="flex flex-col items-center justify-center gap-1 bg-[#C8A75A] text-black hover:bg-white disabled:opacity-40 px-3 py-4 rounded-md text-xs uppercase tracking-widest font-medium">
            <Download className="w-5 h-5" />
            Carte JPG
          </button>
          <button data-testid="wa-download-zip" onClick={downloadZip} disabled={zipping || !vehicle.images?.length} className="flex flex-col items-center justify-center gap-1 border border-[#C8A75A]/50 text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black disabled:opacity-40 px-3 py-4 rounded-md text-xs uppercase tracking-widest font-medium">
            {zipping ? <Loader2 className="w-5 h-5 animate-spin" /> : <Archive className="w-5 h-5" />}
            {zipping ? "ZIP…" : `Toutes les photos (${vehicle.images?.length || 0})`}
          </button>
          <button data-testid="wa-copy-msg" onClick={copyMessage} className="flex flex-col items-center justify-center gap-1 border border-white/20 text-white hover:bg-white/5 px-3 py-4 rounded-md text-xs uppercase tracking-widest">
            <Copy className="w-5 h-5" />
            Copier message
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/25 rounded-md text-amber-200 text-xs leading-relaxed">
          <p className="font-semibold mb-2">Comment envoyer les photos multiples :</p>
          <ol className="list-decimal ml-5 space-y-1">
            <li>Clique <strong>Ouvrir WhatsApp</strong> — le message texte + lien est envoyé au client.</li>
            <li>Clique <strong>Toutes les photos (.zip)</strong> — le ZIP se télécharge sur ton ordi.</li>
            <li>Sur <strong>WhatsApp Web</strong>, décompresse le ZIP et <strong>glisse-dépose les photos</strong> dans la conversation (multi-sélection possible).</li>
            <li>La <strong>Carte JPG</strong> est un teaser unique parfait pour Instagram Stories ou tête de message.</li>
          </ol>
        </div>
      </DialogContent>
    </Dialog>
  );
}
